/**
 * useJobResumption Hook
 * 
 * Resumes polling for pending generation jobs when a flow is loaded.
 * Updates node state when jobs complete or fail.
 * 
 * Enhanced to:
 * - Create output nodes for completed jobs
 * - Add to assets gallery if source node was deleted
 * - Handle all providers (falai, google, openai)
 */

import { useEffect, useRef, useCallback } from 'react';
import { Node, Edge } from '@xyflow/react';
import generationJobClientService, { GenerationJob } from '../services/GenerationJobClientService';
import { supabaseFlowStorageService as flowStorageService } from '../services/SupabaseFlowStorageService';
import { fileStorageService } from '../services/FileStorageService';
import { toastService } from '../components/ui/Toast';
import generationLifecycleService from '../services/GenerationLifecycleService';
import logger from '../utils/logger';
import { markJobPolling, endJobPolling } from '../utils/jobState';

interface UseJobResumptionOptions {
  /** Current nodes in the flow */
  nodes: Node[];
  /** Setter for nodes */
  setNodes: React.Dispatch<React.SetStateAction<Node[]>>;
  /** Current edges in the flow */
  edges: Edge[];
  /** Setter for edges */
  setEdges: React.Dispatch<React.SetStateAction<Edge[]>>;
  /** Function to get a node by ID */
  getNode: (id: string) => Node | undefined;
  /** Whether the flow is loaded and ready */
  isFlowLoaded: boolean;
}

interface PendingPoll {
  job: GenerationJob;
  intervalId: NodeJS.Timeout;
}

/**
 * Hook to resume polling for pending generation jobs
 */
export function useJobResumption({ nodes, setNodes, edges, setEdges, getNode, isFlowLoaded }: UseJobResumptionOptions) {
  const pendingPolls = useRef<Map<string, PendingPoll>>(new Map());
  const hasChecked = useRef(false);

  /**
   * Update a node with generation result
   */
  const updateNodeWithResult = useCallback((nodeId: string, result: {
    videoUrl?: string;
    imageUrl?: string;
    error?: string;
  }) => {
    setNodes((currentNodes) =>
      currentNodes.map((node) => {
        if (node.id !== nodeId) return node;
        
        if (result.error) {
          return {
            ...node,
            data: {
              ...node.data,
              isProcessing: false,
              error: result.error,
              lastExecuted: new Date().toISOString(),
            },
          };
        }

        // Determine output based on what's available
        const output = result.videoUrl || result.imageUrl;
        const outputType = result.videoUrl ? 'video' : 'image';

        return {
          ...node,
          data: {
            ...node.data,
            isProcessing: false,
            output,
            outputType,
            lastExecuted: new Date().toISOString(),
            error: null,
          },
        };
      })
    );
  }, [setNodes]);

  /**
   * Set node to processing state
   */
  const setNodeProcessing = useCallback((nodeId: string, isProcessing: boolean) => {
    setNodes((currentNodes) =>
      currentNodes.map((node) =>
        node.id === nodeId
          ? { ...node, data: { ...node.data, isProcessing } }
          : node
      )
    );
  }, [setNodes]);

  /**
   * Poll a VEO job for completion
   */
  const pollVeoJob = useCallback(async (job: GenerationJob) => {
    try {
      const status = await generationJobClientService.pollVeoStatus(job.operationId);
      
      if (status.done) {
        // Stop polling
        const poll = pendingPolls.current.get(job.id);
        if (poll) {
          clearInterval(poll.intervalId);
          pendingPolls.current.delete(job.id);
        }
        endJobPolling(job.nodeId, job.id);

        if (status.status === 'completed' && status.videoUrl) {
          // Server already persisted it to R2 — don't download + upload a copy.
          updateNodeWithResult(job.nodeId, { videoUrl: status.videoUrl });
          logger.info('[JobResumption] VEO job completed (server-persisted):', job.operationId);
        } else if (status.status === 'completed' && status.videoUri) {
          // Download and store the video
          try {
            const downloadResponse = await fetch(
              `${import.meta.env.VITE_API_URL || ''}/api/veo/video/${encodeURIComponent(job.operationId)}/download`
            );
            
            if (downloadResponse.ok) {
              const videoBlob = await downloadResponse.blob();
              const videoFile = new File([videoBlob], `veo-video-${Date.now()}.mp4`, {
                type: videoBlob.type || 'video/mp4'
              });
              // Flow-scope the upload using the flowId recorded on the job at
              // creation time — without it the video lands under the global
              // prefix and never appears in the flow's asset gallery.
              const fileInfo = await fileStorageService.uploadFile(videoFile, {
                folder: 'generated-videos',
                isPublic: true,
                scope: job.flowId ? 'flow' : undefined,
                flowId: job.flowId || undefined,
                skipDeduplication: true,
              });
              
              updateNodeWithResult(job.nodeId, { videoUrl: fileInfo.url });
              logger.info('[JobResumption] VEO job completed, video stored:', job.operationId);
            } else {
              throw new Error('Failed to download video');
            }
          } catch (downloadError) {
            logger.error('[JobResumption] Failed to download VEO video:', downloadError);
            updateNodeWithResult(job.nodeId, { error: 'Failed to download video' });
          }
        } else {
          updateNodeWithResult(job.nodeId, { error: status.error || 'Video generation failed' });
        }
      }
    } catch (error) {
      logger.error('[JobResumption] VEO poll error:', error);
    }
  }, [updateNodeWithResult]);

  /**
   * Start polling for a pending job
   * Note: For falai jobs, server-side polling handles completion.
   * For google (VEO), we poll from client.
   */
  const startPolling = useCallback((job: GenerationJob) => {
    // Don't poll if already polling this job
    if (pendingPolls.current.has(job.id)) {
      return;
    }

    // Only google (VEO) jobs are polled client-side. fal.ai jobs are polled
    // server-side; any other provider is not client-pollable.
    if (job.provider !== 'google') {
      logger.debug('[JobResumption] Job not client-pollable, skipping client poll:', job.id, job.provider);
      return;
    }

    // Check if the node exists in the current flow
    const nodeExists = nodes.some(n => n.id === job.nodeId);
    if (!nodeExists) {
      // Node was deleted - skip polling, but job will still complete server-side
      // and be delivered via assets gallery on next flow load
      logger.debug('[JobResumption] Node not found in flow, skipping client poll:', job.nodeId);
      return;
    }

    // Set node to processing state
    setNodeProcessing(job.nodeId, true);
    markJobPolling(job.nodeId, job.id);

    // Poll immediately
    pollVeoJob(job);

    // Then poll every 5 seconds
    const intervalId = setInterval(() => pollVeoJob(job), 5000);

    pendingPolls.current.set(job.id, { job, intervalId });
    logger.debug('[JobResumption] Started polling for job:', job.id, job.provider);
  }, [nodes, setNodeProcessing, pollVeoJob]);

  /**
   * Deliver completed job results to nodes
   * This handles jobs that completed while the browser was closed
   * 
   * Enhanced behavior:
   * - If source node was deleted: add to assets gallery + show toast
   * - If source node exists but no output connected: create output node
   * - If output node connected: add as new page to output node
   */
  const deliverCompletedJobs = useCallback((completedJobs: GenerationJob[]) => {
    for (const job of completedJobs) {
      const isVideo = job.jobType === 'video' || (job.resultUrl?.includes('.mp4') ?? false);
      const mediaType = isVideo ? 'video' : 'image';
      
      // Check if the node exists in the current flow
      const sourceNode = nodes.find(n => n.id === job.nodeId);
      
      if (!sourceNode) {
        // Source node was deleted - add result to assets gallery and notify user
        if (job.resultUrl) {
          logger.info('[JobResumption] Source node deleted, adding result to assets gallery:', job.nodeId);
          
          // Notify FilePanel to refresh - the result is already stored in R2 by the server
          window.dispatchEvent(new CustomEvent('fileStorageUpdated'));
        } else if (job.status === 'failed') {
          // Failed job with deleted node - just log, no action needed
          logger.info('[JobResumption] Job failed for deleted node:', job.nodeId, job.errorMessage);
        }
        continue;
      }

      // Source node exists - check if it already has output
      if (sourceNode.data?.output || sourceNode.data?.videoUrl || sourceNode.data?.imageUrl) {
        logger.debug('[JobResumption] Node already has output, skipping:', job.nodeId);
        continue;
      }

      // Handle failed jobs, or jobs with ephemeral fal.ai CDN URLs that were never
      // persisted to R2 (old bug: server fell through to completeJob with dead URL).
      // Scope to falai provider only so fal.ai image jobs are not affected.
      const isEphemeralFalUrl = job.provider === 'falai' && !!job.resultUrl && (
        job.resultUrl.includes('fal.media') ||
        job.resultUrl.includes('fal.run') ||
        job.resultUrl.includes('fal-cdn')
      );

      if (job.status === 'failed' || !job.resultUrl || isEphemeralFalUrl) {
        const errorMsg = job.jobType === 'video'
          ? "The video model had a provider-side hiccup just now. Please try again."
          : "The model had a provider-side hiccup just now. Please try again.";
        updateNodeWithResult(job.nodeId, { error: errorMsg });
        toastService.irisError(errorMsg);
        logger.info('[JobResumption] Delivered failed/expired job to node:', job.nodeId,
          isEphemeralFalUrl ? 'EXPIRED_FAL_URL' : job.status);
        continue;
      }

      // Deliver via the hardened GenerationLifecycleService tail so a resumed
      // job (image OR video) targets the CORRECT output node (run-scoped +
      // fan-grid + no empty/duplicate pages) instead of the old first-edge
      // .find() inline delivery. Resumption is one-shot: 'single' + batchIndex 0.
      generationLifecycleService.deliverMediaOutput(job.nodeId, job.resultUrl, mediaType, 'single', 0);

      // Mark the source node as delivered so it isn't re-processed/re-delivered.
      updateNodeWithResult(job.nodeId, isVideo ? { videoUrl: job.resultUrl } : { imageUrl: job.resultUrl });

      logger.info('[JobResumption] Delivered resumed job via deliverMediaOutput:', job.nodeId);
    }
  }, [nodes, edges, getNode, setNodes, setEdges, updateNodeWithResult]);

  /**
   * Check for pending and completed jobs when flow loads
   */
  useEffect(() => {
    if (!isFlowLoaded || hasChecked.current) {
      return;
    }

    const checkJobs = async () => {
      hasChecked.current = true;
      
      const flowId = flowStorageService.getCurrentFlowId();
      if (!flowId) {
        logger.debug('[JobResumption] No current flow ID');
        return;
      }

      try {
        const { pending: pendingJobs, completed: completedJobs } = await generationJobClientService.getJobsForFlow(flowId);
        
        logger.info('[JobResumption] Found jobs:', { pending: pendingJobs.length, completed: completedJobs.length });

        // First, deliver any completed jobs that finished while browser was closed
        if (completedJobs.length > 0) {
          deliverCompletedJobs(completedJobs);
        }
        
        // Then, start polling for pending jobs
        if (pendingJobs.length > 0) {
          for (const job of pendingJobs) {
            startPolling(job);
          }
        }
      } catch (error) {
        logger.error('[JobResumption] Failed to check jobs:', error);
      }
    };

    checkJobs();
  }, [isFlowLoaded, startPolling, deliverCompletedJobs]);

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      // Clear all polling intervals
      for (const [, poll] of pendingPolls.current) {
        clearInterval(poll.intervalId);
        endJobPolling(poll.job.nodeId, poll.job.id);
      }
      pendingPolls.current.clear();
    };
  }, []);

  /**
   * Reset check flag when flow changes
   */
  useEffect(() => {
    hasChecked.current = false;
  }, [flowStorageService.getCurrentFlowId()]);

  return {
    /** Number of jobs currently being polled */
    pendingJobCount: pendingPolls.current.size,
  };
}

export default useJobResumption;
